import axios from 'axios';
import api from './api';
import type { InventoryBalance, InventoryItem, InventoryItemType, PaginatedResponse, SizeUnit } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/item`;

// ── Error extraction ──────────────────────────────────────────────────────────

const INVENTORY_ERROR_MESSAGES: Record<string, string> = {
  INVENTORY_ITEM_DUPLICATE: 'An item with this name already exists at this venue.',
  INVENTORY_ITEM_INACTIVE: 'This inventory item is inactive.',
  INVENTORY_ITEM_NOT_FOUND: 'Inventory item not found.',
  WINE_NOT_FOUND: 'Wine not found.',
  WINE_INACTIVE: 'This wine is inactive and cannot be tracked in inventory.',
  INVALID_REORDER_POINT: 'Reorder point must be less than or equal to par level.',
};

/**
 * The error as something the UI can ACT on, not just display.
 *
 * INVENTORY_ITEM_EXISTS_IN_COMPANY is the case that matters: the item master is
 * company-scoped, so a product a sister venue already defines cannot be created
 * a second time. That is not a failure the user should have to work around —
 * the right move is to stock the existing one, and the API returns its id
 * precisely so we can offer that instead of a dead end.
 */
export interface InventoryErrorDetail {
  message: string;
  code: string | null;
  /** Present on INVENTORY_ITEM_EXISTS_IN_COMPANY: the master to stock. */
  inventoryItemId: string | null;
  /**
   * ITEM-ACTIVE.2 — present on ITEM_HAS_STOCK: the NET quantity in the way.
   * Carried so the refusal can name the number instead of describing a rule.
   */
  quantity: number | null;
}

/**
 * ITEM-ACTIVE.1 — a bulk flip reports per item, because partial success is the
 * normal outcome: one row with stock must not stop the other ninety-nine, and
 * skipping it silently would tell the operator the list was clean when it is
 * not.
 */
export interface BulkActiveResult {
  requested: number;
  updated: string[];
  skipped: Array<{ itemId: string; displayName: string; code: string; message: string }>;
}

export function extractInventoryErrorDetail(err: unknown): InventoryErrorDetail {
  let code: string | null = null;
  let inventoryItemId: string | null = null;
  let quantity: number | null = null;

  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    if (typeof errorObj?.['code'] === 'string') code = errorObj['code'] as string;
    if (typeof errorObj?.['inventoryItemId'] === 'string') {
      inventoryItemId = errorObj['inventoryItemId'] as string;
    }
    if (typeof errorObj?.['quantity'] === 'number') {
      quantity = errorObj['quantity'] as number;
    }
  }

  return { message: extractInventoryError(err), code, inventoryItemId, quantity };
}

export function extractInventoryError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errorObj?.['code'];
    if (typeof code === 'string' && INVENTORY_ERROR_MESSAGES[code]) {
      return INVENTORY_ERROR_MESSAGES[code];
    }
    const message = errorObj?.['message'] ?? data?.['message'];
    if (typeof message === 'string' && message) return message;
    if (err.response?.status === 409) return INVENTORY_ERROR_MESSAGES['INVENTORY_ITEM_DUPLICATE'];
    if (err.response?.status === 403) return 'You do not have permission to perform this action.';
    if (err.response?.status === 404) return 'Inventory item not found.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WineListLink {
  _id: string;
  wineListId: string;
  wineListName: string;
  wineListStatus: string;
  visibility: 'LIVE' | 'HIDDEN';
  listPrice?: number;
  currency?: string;
  active: boolean;
}

/** A new-Menu membership for an inventory item (distinct from legacy wine lists). */
export interface MenuLink {
  menuId: string;
  menuName: string;
  menuType: string;
  menuStatus: string;
  via: 'item' | 'format';
}

/**
 * Derived stock status. Computed from balance.quantity + item.reorderPoint.
 * OUT  = quantity <= 0
 * LOW  = quantity > 0 AND reorderPoint set AND quantity <= reorderPoint
 * OK   = reorderPoint set AND quantity > reorderPoint
 * null = reorderPoint not set — no alert configured for this item
 */
export type StockStatus = 'OUT' | 'LOW' | 'OK' | 'RESERVED';

/**
 * Balance shape returned by the item list endpoint.
 * The list endpoint returns a venue-wide aggregate (totalQuantity), not a raw balance document.
 * lastCost reflects the most recently updated location that has a cost recorded.
 */
export interface InventoryItemListBalance {
  totalQuantity: number;
  lastCost?: number | null;
}

/** Item as returned by GET /item (balance joined by backend). */
export type InventoryItemWithBalance = InventoryItem & {
  balance: InventoryItemListBalance | null;
  /** Derived by backend once Phase 10 backend is deployed. Optional for backward compat. */
  stockStatus?: StockStatus | null;
  /**
   * INVENTORY-LIST-COLUMNS.1 — venue-wide ACTIVE reserved quantity for this item,
   * and available = totalQuantity − reserved. Quantities: returned to all roles.
   */
  reserved?: number;
  available?: number;
  /**
   * RECIPE-SERVINGS.1 — whole servings a RECIPE item can be assembled into from
   * component stock the venue can reach. Null for every non-recipe item, and
   * null for a recipe with no ingredients yet — unfinished setup is not the same
   * as nothing makeable, so it must not render as 0.
   */
  buildableServings?: number | null;
  /** The component that ran out first — what to restock to raise the count. */
  limitingComponentId?: string | null;
  /**
   * RECIPE-COST-COLUMN.1 — cost of ONE serving in MINOR units. Recipe rows only.
   * Distinct from balance.lastCost, which stays null for a recipe because there
   * is no goods receipt behind it. Money: stripped to null by the backend for
   * callers without stock-costs:view.
   */
  costPerServing?: number | null;
  /** False when an ingredient is unpriced — the cost is then an UNDER-estimate. */
  costComplete?: boolean;
  /**
   * Value on hand in MINOR units (lastCost × totalQuantity via costTimesQty).
   * Money: stripped to null by the backend for callers without stock-costs:view.
   */
  valueOnHand?: number | null;
};

export type CreateItemPayload =
  | {
      /**
       * RECIPE-TYPE.R7 — RECIPE joins this branch so the menu/recipe linking flow
       * can create a recipe-execution item. NOT the canonical InventoryItemType:
       * this payload is a DISCRIMINATED union and WINE has a different shape
       * (wineId, no displayName), so widening to the full union would let a WINE
       * item be created without its wineId. RECIPE must never carry wineId either,
       * which is exactly why it belongs here rather than in the WINE branch.
       *
       * The standard inventory-item form still offers only PURCHASED / WINE /
       * MANUFACTURED — the creation boundary is the UI workflow, not this type.
       *
       * BASIC-ITEM.C6 — BASIC is DELIBERATELY ABSENT and must stay
       * absent, even though it joined the canonical InventoryItemType. A counted
       * item's identity is its (venueId, posItemRef) POS link, which only the
       * internal basic-item resolve endpoint can establish; one created
       * through this generic service would carry no link and could never be
       * resolved by POS or counted through set-on-hand. The API refuses it
       * outright (422 BASIC_ITEM_CREATE_NOT_ALLOWED), and this narrowed union
       * is the compile-time half of that same boundary — pinned by a
       * @ts-expect-error test in inventoryItemService.test.ts.
       */
      type: 'PURCHASED' | 'MANUFACTURED' | 'RECIPE';
      displayName: string;
      /** User-definable category reference (null clears). */
      categoryId?: string | null;
      /** User-definable subcategory; requires categoryId (null clears). */
      subcategoryId?: string | null;
      sizeValue?: number;
      sizeUnit?: SizeUnit;
      parLevel?: number;
      reorderPoint?: number;
      binLocation?: string;
      lotTracked?: boolean;
      /**
       * Phase 7.A.2c — orthogonal to `type`. Defaults to DIRECT_STOCK on the
       * backend. Set RECIPE_CONSUMPTION to create a sellable execution item
       * (e.g. a by-the-glass pour or cocktail) that depletes its recipe
       * components rather than holding stock of its own.
       */
      executionBehavior?: 'DIRECT_STOCK' | 'RECIPE_CONSUMPTION';
      /** POS default selling price in MINOR units (e.g. cents). */
      salePrice?: number | null;
      /** ISO currency for salePrice (sent as the venue default). */
      salePriceCurrency?: string | null;
    }
  | {
      type: 'WINE';
      wineId: string;
      /** User-definable category reference (null clears). */
      categoryId?: string | null;
      /** User-definable subcategory; requires categoryId (null clears). */
      subcategoryId?: string | null;
      sizeValue?: number;
      sizeUnit?: SizeUnit;
      parLevel?: number;
      reorderPoint?: number;
      binLocation?: string;
      lotTracked?: boolean;
      /** POS default selling price in MINOR units (e.g. cents). */
      salePrice?: number | null;
      /** ISO currency for salePrice (sent as the venue default). */
      salePriceCurrency?: string | null;
    };

export type UpdateItemPayload = {
  parLevel?: number;
  reorderPoint?: number;
  active?: boolean;
  binLocation?: string | null;
  /** User-definable category reference; null clears. */
  categoryId?: string | null;
  /** User-definable subcategory; requires categoryId; null clears. */
  subcategoryId?: string | null;
  lotTracked?: boolean;
  /** POS default selling price in MINOR units (e.g. cents); null clears it. */
  salePrice?: number | null;
  /** ISO currency for salePrice; null clears it. */
  salePriceCurrency?: string | null;
  /**
   * SIMPLE-WINE.4 §7 — link a WINE item to a catalogue wine.
   *
   * The backend already has this path: on a WINE-type item it validates the
   * wine, rebuilds displayName from the canonical wine name, and rejects a
   * name collision at the venue with INVENTORY_ITEM_DUPLICATE. Adding the
   * field to the payload type is the whole of the client work — building a
   * second relink mechanism would mean a second set of those rules.
   */
  wineId?: string;
};

// ── Service ───────────────────────────────────────────────────────────────────

const inventoryItemService = {
  /**
   * Get all active inventory items for a venue.
   * Balance is joined per item by the backend — access via item.balance.
   * status param filters by derived stock status (LOW/OUT/OK) — requires Phase 10 backend.
   */
  getItems: async (
    venueId: string,
    page = 1,
    pageSize = 50,
    sort = 'displayName',
    order: 'asc' | 'desc' = 'asc',
    searchQuery?: string,
    locationId?: string,
    status?: StockStatus,
    activeFilter?: 'active' | 'inactive' | 'all',
    typeFilter?: InventoryItemType,
    /** User-definable category filter (sent as categoryId). */
    categoryId?: string,
    /** User-definable subcategory filter (sent as subcategoryId). */
    subcategoryId?: string,
    /**
     * 'company' returns every product the company defines, stocked here or not,
     * each flagged with stockedHere. Default is this venue's own list.
     */
    catalogue?: 'venue' | 'company',
  ): Promise<PaginatedResponse<InventoryItemWithBalance>> => {
    const params: Record<string, unknown> = { page, pageSize, sort, order };
    if (searchQuery?.trim()) params.q = searchQuery.trim();
    if (locationId) params.locationId = locationId;
    if (status) params.status = status;
    if (activeFilter && activeFilter !== 'active') params.active = activeFilter;
    if (typeFilter) params.type = typeFilter;
    if (categoryId) params.categoryId = categoryId;
    if (subcategoryId) params.subcategoryId = subcategoryId;
    if (catalogue === 'company') params.catalogue = 'company';
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /**
   * COUNT-UNSTOCKED.1 — candidate list for the stock-count item picker.
   *
   * Same endpoint as getItems, but asks for zero-stock items as well. A count
   * sheet has to be able to reach an item that was never received, which by
   * definition holds no balance at the location and so is filtered out of the
   * ordinary location-scoped listing that transfers and the item list rely on.
   *
   * A separate method rather than a fourteenth positional argument to getItems,
   * which the call site would have had to reach past six undefineds to use.
   */
  searchForCount: async (
    venueId: string,
    searchQuery: string,
    locationId?: string,
  ): Promise<PaginatedResponse<InventoryItemWithBalance>> => {
    const params: Record<string, unknown> = {
      page: 1, pageSize: 20, sort: 'displayName', order: 'asc',
      includeZeroStock: true,
    };
    if (searchQuery.trim()) params.q = searchQuery.trim();
    if (locationId) params.locationId = locationId;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /** Get a single inventory item (without joined balance). */
  getItem: async (venueId: string, itemId: string): Promise<InventoryItem> => {
    const response = await api.get(`${base(venueId)}/${itemId}`);
    return response.data;
  },

  /** Create a new inventory item. For PURCHASED/MANUFACTURED: displayName is sent directly. For WINE: wineId sent, displayName generated server-side. */
  createItem: async (venueId: string, payload: CreateItemPayload): Promise<InventoryItem> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  /**
   * What THIS venue does with a company product — its own row, not the master.
   *
   *   active: true   stock it here (creates the row; this is how a venue adds
   *                  an existing company product without duplicating it)
   *   active: false  stop listing it here, keeping history. NOT a delete: the
   *                  master is shared, and deleting it would remove it from
   *                  every venue that stocks it.
   *   salePrice      this venue's price for the shared master
   */
  setVenueSettings: async (
    venueId: string,
    itemId: string,
    payload: { active?: boolean; salePrice?: number | null; salePriceCurrency?: string | null },
  ): Promise<InventoryItem> => {
    const response = await api.put(`${base(venueId)}/${itemId}/venue-settings`, payload);
    return response.data;
  },

  /** Update mutable config fields only (parLevel, reorderPoint, active). */
  updateItem: async (
    venueId: string,
    itemId: string,
    payload: UpdateItemPayload,
  ): Promise<InventoryItem> => {
    const response = await api.put(`${base(venueId)}/${itemId}`, payload);
    return response.data;
  },

  /**
   * Soft-delete an inventory item (sets active = false).
   * Operational history is preserved. The item disappears from default list views
   * and remains accessible via the inactive/all filter.
   */
  deleteItem: async (venueId: string, itemId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${itemId}`);
  },

  /**
   * ITEM-ACTIVE.1 — the flag, both ways.
   *
   * Deactivating was only ever spelled "delete", which is why there was no way
   * back: nobody looks for an un-delete button. 409 on the way down when the
   * item still has stock, and on the way up when another active item has taken
   * its name while it was away.
   */
  setActive: async (venueId: string, itemId: string, active: boolean): Promise<InventoryItem> => {
    const response = await api.patch(`${base(venueId)}/${itemId}/active`, { active });
    return response.data;
  },

  /** The same flag over a selection. Partial success is the expected outcome. */
  bulkSetActive: async (
    venueId: string,
    itemIds: string[],
    active: boolean,
  ): Promise<BulkActiveResult> => {
    const response = await api.post(`${base(venueId)}/bulk-active`, { itemIds, active });
    return response.data;
  },

  /** Get all wine list items linked to this inventory item across all wine lists. */
  getWineListLinks: async (venueId: string, itemId: string): Promise<WineListLink[]> => {
    const response = await api.get(`${base(venueId)}/${itemId}/wine-list-links`);
    return response.data;
  },

  /** Get the new-Menu memberships for this inventory item (read-only). */
  getMenuLinks: async (venueId: string, itemId: string): Promise<MenuLink[]> => {
    const response = await api.get(`${base(venueId)}/${itemId}/menu-links`);
    return (response.data?.links ?? []) as MenuLink[];
  },
};

export default inventoryItemService;
